import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextInput,
  Heading,
  Layer,
  CheckBox,
  FormField,
  Select,
  Text,
} from "grommet";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { UserService, AuthService } from "../../services";
import { PhoneInput } from "@shared/";
import { parsePhoneNumber } from "react-phone-number-input";
import { useTranslation } from "react-i18next";
import { Close } from "grommet-icons";
import {
  STAFF_DEPARTMENTS,
  normalizeStaffDepartment,
  staffDepartmentLabel,
} from "./staffDepartments";
import "./AddUser.scss";
import { hasMultipleGroupProperties } from "./staffRoles";
import { presentAlertPrompt } from "../shared/Alert";

const defaultValues = {
  accountType: "individual",
  username: "",
  email: "",
  phone: "",
  department: "Other",
  notifEmail: false,
  notifText: false,
  notifPush: true,
};

const renderSelectOption = (option, index, options, state) => (
  <Box
    className={`staff-user-select-option ${state.active ? "is-active" : ""} ${
      state.selected ? "is-selected" : ""
    }`}
    pad={{ horizontal: "small", vertical: "small" }}
  >
    <Text>{option.label || option}</Text>
  </Box>
);

export default function AddUser(props) {
  const { t } = useTranslation();
  const { userId, onClose, onRefresh, isLoxeAdmin, currentUser, hotel } = props;
  const [user, setUser] = useState(defaultValues);
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [originalRole, setOriginalRole] = useState(null);
  const [originalSms, setOriginalSms] = useState(false);
  const [checkingSms, setCheckingSms] = useState(false);
  const isGroupedHotel = hasMultipleGroupProperties(hotel, isLoxeAdmin);
  const roleDefinitions = [
    {
      value: "admin",
      label: t(
        isGroupedHotel
          ? "addUser.roles.admin.label"
          : "addUser.roles.admin.standaloneLabel",
      ),
      description: t(
        isGroupedHotel
          ? "addUser.roles.admin.description"
          : "addUser.roles.admin.standaloneDescription",
      ),
    },
    {
      value: "manager",
      label: t("addUser.roles.manager.label"),
      description: t("addUser.roles.manager.description"),
    },
    {
      value: "user",
      label: t(
        isGroupedHotel
          ? "addUser.roles.user.label"
          : "addUser.roles.user.standaloneLabel",
      ),
      description: t(
        isGroupedHotel
          ? "addUser.roles.user.description"
          : "addUser.roles.user.standaloneDescription",
      ),
    },
    {
      value: "user-multi",
      label: t("addUser.roles.userMulti.label"),
      description: t("addUser.roles.userMulti.description"),
    },
  ];
  const groupedRoles = isGroupedHotel
    ? ["admin", "manager", "user", "user-multi"]
    : ["admin", "user"];
  const canAssignAdmin = isLoxeAdmin || currentUser?.role === "admin";
  const allowedRoles = roleDefinitions.filter(
    ({ value }) =>
      (groupedRoles.includes(value) && (value !== "admin" || canAssignAdmin)) ||
      value === user.role,
  );
  const selectedRole = roleDefinitions.find(({ value }) => value === user.role);
  const isEditingSelf =
    userId !== 0 && String(userId) === String(currentUser?.id);
  const isProtectedRole = !canAssignAdmin &&
    (originalRole === "admin" || (hotel?.hotelGroup && originalRole === "user"));

  useEffect(() => {
    if (userId !== 0) {
      presentLoading();
      UserService.getUserById(userId).then(
        (response) => {
          dismissLoading();
          if (response.data && response.data.id) {
            setOriginalRole(response.data.role);
            setOriginalSms(Boolean(response.data.notifText));
            setUser({
              ...defaultValues,
              ...response.data,
              accountType: response.data.accountType || "",
              department: normalizeStaffDepartment(response.data.department),
              notifText: Boolean(
                response.data.phone && response.data.notifText,
              ),
            });
          }
        },
        (error) => {
          dismissLoading();
          presentToastErrorContent(error);
        },
      );
    } else {
      setOriginalRole(null);
      setOriginalSms(false);
      setUser(defaultValues);
    }
  }, [userId]);

  function handleInputChange({ target: { name, value } }) {
    var values = { ...user };
    values[name] = value;
    setUser(values);
  }

  const checkSmsAllowance = async () => {
    if (originalSms) return true; // Grandfather existing SMS users, including hotels with several.
    setCheckingSms(true);
    try {
      const { data } = await UserService.getUsers();
      if (data.some(other => other.notifText && String(other.id || other._id) !== String(userId))) {
        presentAlertPrompt({ title: t("addUser.smsLimitTitle"), message: t("addUser.smsLimitMessage"), buttonOKText: t("common.ok") });
        return false;
      }
      return true;
    } catch (error) {
      presentToastErrorContent(error);
      return false;
    } finally { setCheckingSms(false); }
  };

  const handleAdd = async () => {
    if (user.notifText && !(await checkSmsAllowance())) return;
    const values = {
      ...(userId !== 0 && { id: userId }),
      ...(user.accountType && { accountType: user.accountType }),
      username: user.username,
      email: user.email,
      phone: user.phone,
      role: user.role,
      department: user.department,
      notifEmail: user.notifEmail,
      notifText: Boolean(user.phone && user.notifText),
      notifPush: true,
      notificationSchedule: user.notificationSchedule,
    };
    if (userId === 0) {
      setIsSaving(true);
      AuthService.register(values).then(
        async (response) => {
          setIsSaving(false);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          setIsSaving(false);
          presentToastErrorContent(error);
        },
      );
    } else {
      setIsSaving(true);
      UserService.update(values).then(
        async (response) => {
          setIsSaving(false);
          onClose(true);
          presentToastSuccess(t("common.success"));
        },
        (error) => {
          setIsSaving(false);
          presentToastErrorContent(error);
        },
      );
    }
  };

  async function handleCheckboxChange({ target: { name, checked } }) {
    if (name === "notifText" && checked && !user.phone) {
      setPhoneNumberError(t("addUser.phoneRequiredForSms"));
      return;
    }
    if (name === "notifText" && checked && !(await checkSmsAllowance())) return;
    setUser(current => ({ ...current, [name]: checked }));
  }

  const handleTestSend = () => {
    presentLoading();
    const { notifText, notifEmail } = user;
    UserService.testNotification(userId, {
      notifText,
      notifEmail,
      notifPush: true,
    }).then(
      (response) => {
        dismissLoading();
        const invalidPushIdsRemoved = response.data.invalidPushIdsRemoved || 0;
        setTestResults({
          ...response.data.results,
          invalidPushIdsRemoved,
          smsChecked: notifText,
          emailChecked: notifEmail,
          pushChecked: true,
        });
        if (invalidPushIdsRemoved > 0) onRefresh();
      },
      (error) => {
        dismissLoading();
        presentToastErrorContent(error);
      },
    );
  };

  function handlePhoneInputChange(phone) {
    setPhoneNumberError("");
    let phoneObj = phone && parsePhoneNumber(phone);
    let areacode = "+" + ((phoneObj && phoneObj.countryCallingCode) || "");
    setUser({
      ...user,
      phone,
      areacode,
      notifText: phone ? user.notifText : false,
    });
  }

  return (
    <Layer
      position="center"
      onClickOutside={isSaving ? undefined : onClose}
      onEsc={isSaving ? undefined : onClose}
    >
      <div className="staff-user-modal">
        <header className="staff-user-modal__header">
          <Heading level={3} margin="none">
            {userId === 0 ? t("addUser.addTitle") : t("addUser.editTitle")}
          </Heading>
          <Button
            className="staff-user-modal__close"
            icon={<Close size="18px" />}
            onClick={onClose}
            disabled={isSaving}
            plain
            title={t("common.cancel")}
          />
        </header>

        <div className="staff-user-modal__body">
          <div className="staff-user-modal__grid">
            <FormField label={t("addUser.accountType")} border={false} contentProps={{ border: false, focus: false }}
              className="staff-user-modal__field staff-user-modal__field--wide">
              <Select name="accountType" value={user.accountType}
                className="staff-user-modal__control" plain
                aria-label={t("addUser.accountType")}
                options={[
                  ...(!user.accountType ? [{ value: "", label: t("addUser.accountTypes.unclassified") }] : []),
                  { value: "individual", label: t("addUser.accountTypes.individual") },
                  { value: "shared-workstation", label: t("addUser.accountTypes.shared-workstation") },
                ]}
                labelKey="label" valueKey={{ key: "value", reduce: true }}
                onChange={({ value }) => setUser(current => ({ ...current, accountType: value }))}>
                {renderSelectOption}
              </Select>
              <Text size="xsmall" className="staff-user-modal__helper">
                {t(user.accountType === "shared-workstation" ? "addUser.workstationHelp" : user.accountType ? "addUser.individualHelp" : "addUser.unclassifiedHelp")}
              </Text>
            </FormField>
            <FormField
              className="staff-user-modal__field"
              label={t("settingsUsers.colEmail")}
              border={false} contentProps={{ border: false, focus: false }}
            >
              <TextInput
                className="staff-user-modal__control"
                value={user.email}
                name="email"
                onChange={handleInputChange}
                plain
              />
            </FormField>
            <FormField
              className="staff-user-modal__field"
              label={t("settingsUsers.colRole")}
              border={false} contentProps={{ border: false, focus: false }}
            >
              <Select
                className="staff-user-modal__control"
                name="role"
                placeholder={t("addUser.selectRole")}
                value={user.role}
                options={allowedRoles}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                disabled={(isEditingSelf && !isLoxeAdmin) || isProtectedRole}
                onChange={({ value: nextValue }) => {
                  setUser({ ...user, role: nextValue });
                }}
                plain
              >
                {renderSelectOption}
              </Select>
              {selectedRole && (
                <Text className="staff-user-modal__helper" size="xsmall">
                  {selectedRole.description}
                </Text>
              )}
            </FormField>
            <FormField
              className="staff-user-modal__field"
              label={t(user.accountType === "shared-workstation" ? "addUser.workstationName" : "settingsUsers.colUsername")}
              border={false} contentProps={{ border: false, focus: false }}
            >
              <TextInput
                className="staff-user-modal__control"
                value={user.username}
                name="username"
                onChange={handleInputChange}
                plain
              />
            </FormField>
            <FormField
              label={t("addUser.phoneNumber")}
              className="staff-user-modal__field staff-user-modal__phone"
              error={phoneNumberError}
              border={false} contentProps={{ border: false, focus: false }}
            >
              <PhoneInput
                value={user.phone}
                onChange={handlePhoneInputChange}
              />
            </FormField>
            <FormField
              className="staff-user-modal__field staff-user-modal__field--wide"
              label={t("settingsUsers.colDepartment")}
              border={false} contentProps={{ border: false, focus: false }}
            >
              <Select
                className="staff-user-modal__control"
                name="department"
                value={user.department || "Other"}
                options={STAFF_DEPARTMENTS.map((value) => ({ value, label: staffDepartmentLabel(value, t) }))}
                labelKey="label"
                valueKey={{ key: "value", reduce: true }}
                onChange={({ value: nextValue }) => {
                  setUser({ ...user, department: nextValue });
                }}
                plain
              >
                {renderSelectOption}
              </Select>
            </FormField>
          </div>

          <section className="staff-user-modal__notifications">
            <Text className="staff-user-modal__section-title">
              {t("addUser.receiveNotifications")}
            </Text>
            <Text size="small">{t(user.accountType === "shared-workstation" ? "addUser.workstationNotifications" : "addUser.pushFirstHelp")}</Text>
            <div className="staff-user-modal__checks">
              <CheckBox
                name="notifEmail"
                checked={user.notifEmail}
                onChange={handleCheckboxChange}
                label={t("addUser.emailNotification")}
              />
              <CheckBox
                name="notifText"
                disabled={checkingSms || isSaving}
                checked={user.notifText}
                onChange={handleCheckboxChange}
                label={t("addUser.textNotification")}
              />
            </div>
          </section>
        </div>

        <footer className="staff-user-modal__footer">
          <div>
            {userId !== 0 && (
              <Button
                className="staff-user-modal__test"
                label={t("addUser.testSend")}
                onClick={handleTestSend}
                color="neutral-2"
              />
            )}
          </div>
          <div className="staff-user-modal__actions">
            <Button
              label={t("common.cancel")}
              onClick={onClose}
              color="dark-3"
            />
            <Button
              label={userId === 0 ? t("common.add") : t("common.update")}
              onClick={handleAdd}
              disabled={isSaving || checkingSms}
              primary
              color="status-ok"
            />
          </div>
        </footer>
      </div>
      {testResults && (
        <Layer
          position="center"
          onClickOutside={() => setTestResults(null)}
          onEsc={() => setTestResults(null)}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Heading level={4} margin="none">
              {t("addUser.testSendResults")}
            </Heading>
            <Box gap="xsmall">
              {testResults.smsChecked && (
                <Box direction="row" gap="small" align="center">
                  <Text color={testResults.sms ? "status-ok" : "status-error"}>
                    {testResults.sms ? "✓" : "✗"}
                  </Text>
                  <Text>
                    {t("addUser.smsResult", {
                      status: testResults.sms
                        ? t("addUser.sent")
                        : t("addUser.failed"),
                    })}
                  </Text>
                </Box>
              )}
              {testResults.emailChecked && (
                <Box direction="row" gap="small" align="center">
                  <Text
                    color={testResults.email ? "status-ok" : "status-error"}
                  >
                    {testResults.email ? "✓" : "✗"}
                  </Text>
                  <Text>
                    {t("addUser.emailResult", {
                      status: testResults.email
                        ? t("addUser.sent")
                        : t("addUser.failed"),
                    })}
                  </Text>
                </Box>
              )}
              {testResults.pushChecked && (
                <Box direction="row" gap="small" align="center">
                  <Text color={testResults.push ? "status-ok" : "status-error"}>
                    {testResults.push ? "✓" : "✗"}
                  </Text>
                  <Text>
                    {t("addUser.pushResult", {
                      status: testResults.push
                        ? t("addUser.sent")
                        : t("addUser.failed"),
                    })}
                  </Text>
                </Box>
              )}
              {testResults.invalidPushIdsRemoved > 0 && (
                <Text size="small" color="status-error">
                  {t("addUser.invalidPushIdsRemoved", {
                    count: testResults.invalidPushIdsRemoved,
                  })}
                </Text>
              )}
            </Box>
            <Box direction="row" justify="end">
              <Button
                label={t("common.close")}
                onClick={() => setTestResults(null)}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Layer>
  );
}

AddUser.propTypes = {};
AddUser.defaultProps = {};
